
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AuthResponse } from '@supabase/supabase-js';

export function usePasswordRecoverySession() {
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const [sessionError, setSessionError] = useState<string>("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const checkSession = async () => {
      try {
        console.log('Checking for password recovery session...');
        console.log('Current URL search params:', searchParams.toString());
        
        // Method 1: Check URL fragments for password recovery tokens
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const type = searchParams.get('type');
        
        // Method 2: Check URL hash for tokens (alternative method)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashAccessToken = hashParams.get('access_token');
        const hashRefreshToken = hashParams.get('refresh_token');
        const hashType = hashParams.get('type');
        
        console.log('URL params - type:', type, 'access_token present:', !!accessToken);
        console.log('Hash params - type:', hashType, 'access_token present:', !!hashAccessToken);
        
        const finalAccessToken = accessToken || hashAccessToken;
        const finalRefreshToken = refreshToken || hashRefreshToken;
        const finalType = type || hashType;
        
        if (finalType === 'recovery' && finalAccessToken) {
          console.log('Password recovery link detected, setting session...');
          
          try {
            // Set the session using the tokens from the URL
            const { data, error }: AuthResponse = await supabase.auth.setSession({
              access_token: finalAccessToken,
              refresh_token: finalRefreshToken || '',
            });
            
            if (error) {
              console.error('Error setting recovery session:', error);
              setSessionError('Invalid or expired recovery link. Please request a new password reset.');
              setIsValidSession(false);
            } else if (data.session) {
              console.log('Recovery session set successfully');
              setIsValidSession(true);
              setSessionError("");
              
              // Clean up URL by removing the tokens
              const newUrl = window.location.pathname;
              window.history.replaceState({}, document.title, newUrl);
            } else {
              console.error('No session data returned');
              setSessionError('Failed to establish recovery session. Please try again.');
              setIsValidSession(false);
            }
          } catch (sessionError) {
            console.error('Exception during session setup:', sessionError);
            setSessionError('Failed to establish recovery session. Please try again.');
            setIsValidSession(false);
          }
          return;
        }
        
        // Method 3: Set up auth state listener for PASSWORD_RECOVERY event
        console.log('Setting up auth state listener for PASSWORD_RECOVERY...');
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('Auth state change:', event, session ? 'Session exists' : 'No session');
            
            if (event === 'PASSWORD_RECOVERY' && session) {
              console.log('PASSWORD_RECOVERY event detected');
              setIsValidSession(true);
              setSessionError("");
            } else if (event === 'SIGNED_IN' && session) {
              // Check if this could be a recovery session
              console.log('User signed in, checking session validity');
              setIsValidSession(true);
              setSessionError("");
            } else if (event === 'SIGNED_OUT') {
              console.log('User signed out');
              setIsValidSession(false);
              setSessionError('Session expired. Please request a new password reset.');
            }
          }
        );
        
        unsubscribe = () => subscription.unsubscribe();
        
        // Method 4: Check if user already has an active session
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('User already has active session');
          setIsValidSession(true);
          setSessionError("");
        } else {
          console.log('No valid session found');
          setSessionError('No active password recovery session. Please click the reset link from your email.');
          setIsValidSession(false);
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setSessionError('An error occurred while verifying your session. Please try again.');
        setIsValidSession(false);
      }
    };

    checkSession();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        console.log('Cleaning up auth state listener');
        unsubscribe();
      }
    };
  }, [searchParams]);

  return { isValidSession, sessionError };
}
